import type { DeliveryEvent, DeliveryOption, DeliveryZone } from 'src/redux/delivery/types';
import { deliveryOptionArrivesOnOrByDate } from 'src/redux/delivery/utils/deliveryOptionArrivesOnOrByDate';
import { getCutoffMessage } from 'src/redux/delivery/utils/getCutoffMessage';
import { getDeliveryOptionText } from 'src/redux/delivery/utils/getDeliveryOptionText';
import { getFormattedDeliveryDate } from 'src/redux/delivery/utils/getFormattedDeliveryDate';
import { getFormattedPrice } from 'src/redux/delivery/utils/getFormattedPrice';

export interface DeliverySummaryPrimary {
  dateTime?: string;
  prefix: string;
  price: string;
  value: string;
}

export interface DeliverySummarySecondary {
  dateTime?: string;
  price: string;
  providerText: string;
  value: string;
}

export interface DeliverySummaryContent {
  orderByText: string | null;
  primary: DeliverySummaryPrimary;
  secondary: DeliverySummarySecondary | null;
}

export interface FreePartnerDelivery {
  formattedThreshold: string;
  partnerName: string;
}

export interface DeliverySummaryViewModel {
  content: DeliverySummaryContent | null;
  deliveryEventName?: string;
  freePartnerDelivery: FreePartnerDelivery | null;
}

export interface AdaptDeliveryToSummaryArgs {
  deliveryEvent?: DeliveryEvent | null;
  partnerName?: string;
  selectedDeliveryZone?: DeliveryZone;
}

const getFreePartnerDelivery = (
  deliveryOptions: DeliveryOption[],
  partnerName?: string,
): FreePartnerDelivery | null => {
  const hasFreeOption = deliveryOptions.some((option) => Number(option.price.amount) === 0);
  const thresholdOption = deliveryOptions.find(
    (option) => option.partnerFreeDeliveryThresholdPrice,
  );

  if (!partnerName || hasFreeOption || !thresholdOption?.partnerFreeDeliveryThresholdPrice) {
    return null;
  }

  const { amount, currencyCode } = thresholdOption.partnerFreeDeliveryThresholdPrice;

  return {
    formattedThreshold: getFormattedPrice(Number(amount), currencyCode),
    partnerName,
  };
};

const formatValue = (option: DeliveryOption): string =>
  option.exactDate && option.date
    ? getFormattedDeliveryDate(option.date)
    : `${option.min}-${option.max} days`;

const formatDateTime = (option: DeliveryOption): string | undefined =>
  option.exactDate && option.date ? option.date : undefined;

const formatPrice = (option: DeliveryOption): string =>
  getFormattedPrice(Number(option.price.amount), option.price.currencyCode);

export const adaptDeliveryToSummary = ({
  deliveryEvent,
  partnerName,
  selectedDeliveryZone,
}: AdaptDeliveryToSummaryArgs): DeliverySummaryViewModel => {
  const deliveryOptions = selectedDeliveryZone?.deliveryOptions || [];
  const hasOptionThatArrivesByEvent = deliveryEvent
    ? deliveryOptions.some((option) => deliveryOptionArrivesOnOrByDate(option, deliveryEvent.date))
    : false;
  const deliveryEventName =
    deliveryEvent && hasOptionThatArrivesByEvent ? deliveryEvent.name : undefined;
  const freePartnerDelivery = getFreePartnerDelivery(deliveryOptions, partnerName);

  if (deliveryOptions.length === 0) {
    return { content: null, deliveryEventName, freePartnerDelivery };
  }

  const [option1, option2] = deliveryOptions;
  const cutoffMessage = getCutoffMessage(option1.cutoffDateTime);
  const specialSize = selectedDeliveryZone?.exceptional || false;

  const prefix = specialSize
    ? 'Special size delivery '
    : option1.optionCode === 'SATURDAY'
    ? 'Get it on '
    : 'Estimated delivery: ';

  const primary: DeliverySummaryPrimary = {
    prefix,
    value: formatValue(option1),
    dateTime: formatDateTime(option1),
    price: formatPrice(option1),
  };

  const secondary: DeliverySummarySecondary | null = option2
    ? {
        value: formatValue(option2),
        dateTime: formatDateTime(option2),
        price: formatPrice(option2),
        providerText: getDeliveryOptionText(option2.optionCode),
      }
    : null;

  return {
    content: {
      orderByText: cutoffMessage ? `Order by ${cutoffMessage}` : null,
      primary,
      secondary,
    },
    deliveryEventName,
    freePartnerDelivery,
  };
};
